export type Device = {
  id: string
  displayName: string
  groupName: string
  operatorName: string
  platform: string
  osVersion: string
  appVersion: string
  timezone: string
  lastSeenAtMillis: number
  detectionStatus: string
  detectionDetail: string
  revoked: boolean
  inCallCount: number
  ringingCount: number
  appCount: number
  syncHealth: string
}

export type ManagedApp = {
  packageName: string
  displayName: string
  accountAlias: string
  receivingPreference: number
  coinsPerMinute: number
  yuanPerHundredCoins: number
  priceConfirmed: boolean
  status: string
  evidence: string
  confidence?: number
  observedAtMillis: number
  expiresAtMillis: number
  stateExpired: boolean
  signalTypes: string
}

export type DeviceDetail = Device & { apps: ManagedApp[] }

export type AppInsight = {
  deviceId: string
  deviceName: string
  packageName: string
  displayName: string
  opportunityCount: number
  completedCallCount: number
  callMillis: number
  overlappingCount: number
  estimatedValueYuan: number
  effectiveWorkMillis: number
  hourlyValueYuan?: number
}

export type Insights = {
  range: string
  fromMillis: number
  toMillis: number
  totals: { opportunityCount: number; completedCallCount: number; callMillis: number; overlappingCount: number; estimatedValueYuan: number }
  apps: AppInsight[]
  deviceRecommendations: AppInsight[]
  attention: Array<{ deviceId: string; deviceName: string; packageName?: string; displayName?: string; status: string; evidence: string; observedAtMillis: number; stateExpired: boolean; syncWarning: boolean }>
  timeSlots: Array<{ label: string; opportunityCount: number; completedCallCount: number; overlappingCount: number; callMillis: number; estimatedValueYuan: number }>
  dayGroups: Array<{ group: string; label: string; opportunityCount: number; completedCallCount: number; overlappingCount: number; callMillis: number; estimatedValueYuan: number }>
  pricePerformance: Array<{ deviceId: string; deviceName: string; packageName: string; displayName: string; coinsPerMinute: number; yuanPerHundredCoins: number; completedCallCount: number; callMillis: number; estimatedValueYuan: number }>
  work: Array<{ deviceId: string; deviceName: string; workMillis: number }>
  dataQuality: { deviceCount: number; syncWarningCount: number; unknownStateCount: number; expiredStateCount: number }
}

export type AuthState = {
  username: string
  authenticated?: boolean
  organizationId?: string
  passwordChangeRequired: boolean
}

const csrf = () => document.cookie.split('; ').find(v => v.startsWith('CHATSHIFT_CSRF='))?.split('=')[1] ?? ''

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  if (init.body) headers.set('Content-Type', 'application/json')
  if (!['GET', 'HEAD'].includes(method)) headers.set('X-CSRF-Token', decodeURIComponent(csrf()))
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.detail ?? body.message ?? `请求失败（${response.status}）`)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  me: () => request<AuthState>('/api/v1/auth/me'),
  login: (username: string, password: string) => request<AuthState>('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  recovery: (username: string, recoveryCode: string) => request<AuthState>('/api/v1/auth/recovery', { method: 'POST', body: JSON.stringify({ username, recoveryCode }) }),
  changePassword: (currentPassword: string, newPassword: string) => request<{ passwordChangeRequired: boolean }>('/api/v1/auth/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  logout: () => request('/api/v1/auth/logout', { method: 'POST' }),
  devices: () => request<Device[]>('/api/v1/devices'),
  device: (id: string) => request<DeviceDetail>(`/api/v1/devices/${id}`),
  insights: (range: string, deviceId?: string) => request<Insights>(`/api/v1/insights?range=${range}${deviceId ? `&deviceId=${deviceId}` : ''}`),
  updateDevice: (id: string, body: object) => request(`/api/v1/devices/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  revoke: (id: string) => request(`/api/v1/devices/${id}/revoke`, { method: 'POST' }),
  deleteHistory: (id: string, confirmDeviceName: string) => request(`/api/v1/devices/${id}/history`, { method: 'DELETE', body: JSON.stringify({ confirmDeviceName }) })
}
